<!-- Failure drawer (PBI 55100 / 55097). Matches designs/execute-fail.html: a
     right-side drawer with a "Mark as failed" header, the What happened / affected
     patient / attachments / severity fields, and Cancel / Submit failure actions. -->
<div class="drawer-header">
  <div class="row">
    <span class="fail-tag">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        width="11"
        height="11"
      >
        <path d="M6 6l12 12M6 18L18 6" />
      </svg>
      Mark as failed
    </span>
    <button type="button" class="drawer-close" aria-label="Close" (click)="close()">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        width="18"
        height="18"
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  </div>
  <h2>{{ data.testCaseName || 'Fail test case' }}</h2>
  @if (data.planContext) {
    <div class="drawer-sub">{{ data.planContext }}</div>
  }
</div>

@if (savedResult(); as saved) {
  <!-- Saved. The links below are the ones the API just issued; they expire, so they're
       rendered from the response rather than kept anywhere. -->
  <div class="drawer-body">
    <div class="saved-note">Failure submitted with {{ saved.attachments?.length }} file(s).</div>
    <div class="form-group">
      <label class="form-label">Attached files</label>
      <app-attachment-list [attachments]="saved.attachments ?? []" />
    </div>
  </div>
  <div class="drawer-footer">
    <button type="button" class="btn btn-primary" (click)="close()">Done</button>
  </div>
} @else {
  <form [formGroup]="form" (ngSubmit)="save()" class="drawer-form">
    <div class="drawer-body">
      <div class="form-group">
        <label class="form-label" for="what-happened">What happened?</label>
        <textarea
          id="what-happened"
          class="form-textarea"
          formControlName="comment"
          placeholder="Describe what you saw vs. what you expected. Reproduction steps help a lot."
        ></textarea>
        <div class="form-hint">
          Most helpful when you include the exact text shown and the time it happened. Attach
          screenshots or logs below — no need to paste links.
        </div>
        @if (form.controls.comment.touched && form.controls.comment.hasError('required')) {
          <div class="field-error">Please describe what happened.</div>
        }
      </div>

      <div class="form-group">
        <label class="form-label" for="patient">Affected patient (optional)</label>
        <input
          id="patient"
          class="form-input"
          type="text"
          formControlName="patient"
          placeholder="MRN or sample patient"
        />
        <div class="form-hint">
          Only include test or sample patient identifiers, never real PHI.
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">Screenshots (optional)</label>

        <!-- Thumbnail grid, with the add tile as its last cell until the cap. The whole
             grid is the drop target and lights up for a drag anywhere on the drawer. -->
        <div
          class="shot-grid"
          [class.is-dragover]="isDraggingOver()"
          [class.is-empty]="!attachments().length"
        >
          @for (a of attachments(); track a.id) {
            <figure class="shot" [class.shot-failed]="a.status === 'error'">
              <div class="shot-preview">
                @if (a.previewUrl) {
                  <img [src]="a.previewUrl" [alt]="'Preview of ' + a.fileName" />
                } @else {
                  <span class="shot-no-preview" aria-hidden="true">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      width="20"
                      height="20"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </span>
                }
                @if (a.status === 'uploading') {
                  <div
                    class="shot-progress"
                    role="progressbar"
                    [attr.aria-valuenow]="a.percent"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    [attr.aria-label]="'Uploading ' + a.fileName"
                  >
                    <div class="shot-progress-bar" [style.width.%]="a.percent"></div>
                  </div>
                }
              </div>

              <button
                type="button"
                class="shot-remove"
                [attr.aria-label]="'Remove ' + a.fileName"
                (click)="removeAttachment(a.id)"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  width="10"
                  height="10"
                  aria-hidden="true"
                >
                  <path d="M6 6l12 12M6 18L18 6" />
                </svg>
              </button>

              <figcaption class="shot-meta">
                <span class="shot-name" [title]="a.fileName">{{ a.fileName }}</span>
                <span class="shot-size">{{ formatSize(a.sizeBytes) }}</span>
                @switch (a.status) {
                  @case ('uploading') {
                    <span class="shot-status">Uploading… {{ a.percent }}%</span>
                  }
                  @case ('uploaded') {
                    <span class="shot-status shot-status-done">Uploaded</span>
                  }
                  @case ('error') {
                    <span class="shot-status shot-status-failed">{{ a.error }}</span>
                  }
                }
              </figcaption>
            </figure>
          }

          @if (canAddMore()) {
            <label class="shot-add" for="attachments">
              <span class="shot-add-plus" aria-hidden="true">+</span>
              <span class="shot-add-label">Add another</span>
              <span class="shot-add-hint">or drop image here</span>
            </label>
          }
        </div>

        <p class="shot-sub">
          <span class="shot-count" [class.at-cap]="!canAddMore()">
            {{ attachments().length }} attached
          </span>
          · up to {{ maxAttachments }} · {{ allowedFormats }} up to {{ maxSizeLabel }} each
        </p>

        <input
          #attachmentInput
          id="attachments"
          type="file"
          multiple
          [attr.accept]="accept"
          hidden
          (change)="onFilesSelected($event)"
        />
        @if (rejections().length) {
          <div class="field-error" role="alert">
            @for (warning of rejections(); track warning) {
              <span class="rejection">{{ warning }}</span>
            }
          </div>
        }
        @if (pickError()) {
          <div class="field-error">{{ pickError() }}</div>
        }
      </div>

      <div class="form-group">
        <label class="form-label">Severity</label>
        <div class="severity-chips" role="group" aria-label="Failure severity">
          @for (s of severities; track s) {
            <button
              type="button"
              class="sev-chip"
              [class.active]="form.value.severity === s"
              [class.showstopper]="s === 'Showstopper'"
              [attr.aria-pressed]="form.value.severity === s"
              (click)="selectSeverity(s)"
            >
              @if (s === 'Showstopper') {
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="11"
                  height="11"
                  aria-hidden="true"
                >
                  <path d="M12 2 1 21h22L12 2zm-1 9v5h2v-5h-2zm0 6v2h2v-2h-2z" />
                </svg>
              }
              {{ s }}
            </button>
          }
        </div>
        @if (form.controls.severity.touched && form.controls.severity.hasError('required')) {
          <div class="field-error">Select a severity.</div>
        }
      </div>

      @if (serverError()) {
        <div class="server-error">{{ serverError() }}</div>
      }
    </div>

    <div class="drawer-footer">
      @if (submitHint(); as hint) {
        <!-- Lives in the footer, not the scrolling body, so the reason is always beside
             the button it explains. -->
        <p class="submit-hint" aria-live="polite">{{ hint }}</p>
      }
      <button type="button" class="btn btn-ghost" (click)="cancel()">Cancel</button>
      <button type="submit" class="btn btn-fail" [disabled]="submitBlocked()">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          width="14"
          height="14"
        >
          <path d="M6 6l12 12M6 18L18 6" />
        </svg>
        {{ saving() ? 'Submitting…' : isUploading() ? 'Uploading…' : 'Submit failure' }}
      </button>
    </div>
  </form>
}
